import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../api/client';
import { ChapterMemberSearchDropdown } from './ChapterMemberSearchDropdown';

export type FundraisingType =
  | 'VirtualWalkRun'
  | 'TshirtSale'
  | 'Sponsorship'
  | 'Donation'
  | 'GeneralOther';

interface FundraisingActivity {
  id: string;
  name: string;
  type: FundraisingType;
  description: string;
  goalAmount: number;
  costPerMile?: number | null;
  startDate: string;
  endDate: string;
  totalRaised?: number;
}

interface ActivityEntriesResponse {
  type: FundraisingType;
  entries: Record<string, unknown>[];
}

interface EntryFieldDef {
  key: string;
  label: string;
  inputType: string;
}

export interface ActivityEntriesModalProps {
  activity: FundraisingActivity;
  onClose: () => void;
  onDataChanged: () => void;
  canEdit: boolean;
}

const TYPE_LABELS: Record<FundraisingType, string> = {
  VirtualWalkRun: 'Virtual Walk/Run',
  TshirtSale: 'T-Shirt Sale',
  Sponsorship: 'Sponsorship',
  Donation: 'Donation',
  GeneralOther: 'General/Other',
};

const TYPE_COLORS: Record<FundraisingType, string> = {
  VirtualWalkRun: '#1a6b3c',
  TshirtSale: '#c9a84c',
  Sponsorship: '#0f2d1f',
  Donation: '#4a90d9',
  GeneralOther: '#666',
};

function getEntryFieldsForType(type: FundraisingType): EntryFieldDef[] {
  switch (type) {
    case 'VirtualWalkRun':
      return [
        { key: 'participantName', label: 'Participant', inputType: 'member-search' },
        { key: 'sponsorName', label: 'Sponsor Name', inputType: 'text' },
        { key: 'milesSponsored', label: 'Miles Sponsored', inputType: 'number' },
        { key: 'sponsoredDate', label: 'Sponsored Date', inputType: 'date' },
        { key: 'entryDate', label: 'Entry Date', inputType: 'date' },
      ];
    case 'TshirtSale':
      return [
        { key: 'size', label: 'Size', inputType: 'text' },
        { key: 'style', label: 'Style', inputType: 'text' },
        { key: 'quantitySold', label: 'Quantity Sold', inputType: 'number' },
        { key: 'pricePerItem', label: 'Price Per Item', inputType: 'number' },
      ];
    case 'Sponsorship':
      return [
        { key: 'sponsorName', label: 'Sponsor Name', inputType: 'text' },
        { key: 'tier', label: 'Tier', inputType: 'text' },
        { key: 'pledgedAmount', label: 'Pledged Amount', inputType: 'number' },
        { key: 'receivedAmount', label: 'Received Amount', inputType: 'number' },
      ];
    case 'Donation':
      return [
        { key: 'donorName', label: 'Donor Name (optional)', inputType: 'text' },
        { key: 'donationAmount', label: 'Donation Amount', inputType: 'number' },
        { key: 'donationDate', label: 'Donation Date', inputType: 'date' },
      ];
    case 'GeneralOther':
      return [
        { key: 'description', label: 'Description', inputType: 'text' },
        { key: 'amount', label: 'Amount', inputType: 'number' },
      ];
  }
}

function formatCellValue(key: string, val: unknown): string {
  if (val === null || val === undefined) {
    if (key === 'donorName') return 'Anonymous';
    return '—';
  }
  if (typeof val === 'number') {
    const isCurrency =
      key.toLowerCase().includes('amount') ||
      key.toLowerCase().includes('revenue') ||
      key.toLowerCase().includes('price') ||
      key.toLowerCase().includes('total');
    return isCurrency
      ? `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : val.toLocaleString();
  }
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
    return new Date(val).toLocaleDateString();
  }
  return String(val);
}

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff', borderRadius: '12px', width: '90vw',
    maxWidth: '1100px', maxHeight: '90vh', display: 'flex',
    flexDirection: 'column', boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
    overflow: 'hidden',
  },
  header: {
    padding: '1.25rem 1.5rem', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: '#f9fafb',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  badge: {
    display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '4px',
    fontSize: '0.7rem', fontWeight: 700, color: '#fff', textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  title: { fontSize: '1.25rem', fontWeight: 700, color: '#0f2d1f', margin: 0 },
  closeBtn: {
    background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer',
    color: '#666', padding: '4px 8px', borderRadius: '4px', lineHeight: 1,
  },
  toolbar: {
    padding: '0.75rem 1.5rem', borderBottom: '1px solid #e5e7eb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap' as const, gap: '0.5rem',
  },
  body: { padding: '1.5rem', overflowY: 'auto' as const, flex: 1 },
  btn: {
    padding: '0.45rem 0.85rem', backgroundColor: '#1a6b3c', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600,
    cursor: 'pointer',
  },
  btnGold: {
    padding: '0.45rem 0.85rem', backgroundColor: '#c9a84c', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600,
    cursor: 'pointer',
  },
  btnDanger: {
    padding: '0.45rem 0.85rem', backgroundColor: '#d32f2f', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600,
    cursor: 'pointer',
  },
  btnSecondary: {
    padding: '0.45rem 0.85rem', backgroundColor: '#e0e0e0', color: '#333',
    border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600,
    cursor: 'pointer',
  },
};

const tableStyles: Record<string, React.CSSProperties> = {
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th: {
    textAlign: 'left', padding: '0.6rem 0.75rem', borderBottom: '2px solid #e5e7eb',
    fontWeight: 600, color: '#0f2d1f', fontSize: '0.8rem', whiteSpace: 'nowrap' as const,
  },
  td: { padding: '0.6rem 0.75rem', borderBottom: '1px solid #f0f0f0', color: '#333' },
  trHover: { backgroundColor: '#f9fafb' },
  iconBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px',
    fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1,
    transition: 'background-color 0.15s',
  },
  footer: {
    borderTop: '2px solid #0f2d1f', fontWeight: 700, backgroundColor: '#f5f5f5',
  },
};

const formStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: '1.25rem', backgroundColor: '#f9fafb', borderRadius: '8px',
    marginBottom: '1.5rem', border: '1px solid #e5e7eb',
  },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
  field: { marginBottom: '0.5rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  input: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.85rem', boxSizing: 'border-box' as const, width: '100%',
  },
  error: {
    color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem',
    borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.8rem',
  },
};

export function ActivityEntriesModal({ activity, onClose, onDataChanged, canEdit }: ActivityEntriesModalProps) {
  const [entries, setEntries] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Add entry form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFields, setAddFields] = useState<Record<string, string>>({});
  const [addSaving, setAddSaving] = useState(false);

  // Edit entry
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState<Record<string, string>>({});
  const [editSaving, setEditSaving] = useState(false);

  // Duplicate warning
  const [duplicateWarning, setDuplicateWarning] = useState('');

  // Keys that identify a potential duplicate per activity type
  const duplicateCheckKeys: Record<string, string[]> = {
    Sponsorship: ['sponsorName'],
    GeneralOther: ['description'],
    Donation: ['donorName'],
    VirtualWalkRun: ['sponsorName'],
    TshirtSale: ['size', 'style'],
  };

  function checkDuplicate(fieldKey: string, value: string) {
    const keysToCheck = duplicateCheckKeys[activity.type] || [];
    if (!keysToCheck.includes(fieldKey) || !value.trim()) {
      setDuplicateWarning('');
      return;
    }
    const normalizedValue = value.trim().toLowerCase();
    const match = entries.find((e) => {
      const existing = e[fieldKey];
      return typeof existing === 'string' && existing.trim().toLowerCase() === normalizedValue;
    });
    if (match) {
      const label = getEntryFieldsForType(activity.type).find(f => f.key === fieldKey)?.label || fieldKey;
      setDuplicateWarning(`⚠️ Duplicate: an entry with ${label} "${value.trim()}" already exists.`);
    } else {
      setDuplicateWarning('');
    }
  }

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchEntries();
  }, [activity.id]);

  async function fetchEntries() {
    setLoading(true);
    try {
      const data = await apiClient<ActivityEntriesResponse>(
        `/fundraising/activities/${activity.id}/entries`
      );
      setEntries(data.entries);
    } catch {
      setError('Failed to load entries.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddEntry(e: FormEvent) {
    e.preventDefault();
    setAddSaving(true);
    setError('');
    setDuplicateWarning('');
    try {
      const fields = getEntryFieldsForType(activity.type);
      const numericKeys = fields.filter((f) => f.inputType === 'number').map((f) => f.key);
      const body: Record<string, unknown> = {};
      for (const field of fields) {
        const value = addFields[field.key] || '';
        if (field.inputType === 'member-search') {
          if (value) body[field.key] = value;
        } else if (numericKeys.includes(field.key) && value !== '') {
          body[field.key] = field.key === 'quantitySold' ? parseInt(value, 10) : parseFloat(value);
        } else if (value !== '') {
          body[field.key] = value;
        }
      }
      await apiClient(`/fundraising/activities/${activity.id}/entries`, { method: 'POST', body });
      // Reset fields with today's dates pre-filled, keep form open
      const today = new Date().toISOString().split('T')[0];
      const defaults: Record<string, string> = {};
      getEntryFieldsForType(activity.type).forEach((f) => {
        if (f.inputType === 'date') defaults[f.key] = today;
      });
      setAddFields(defaults);
      await fetchEntries();
      onDataChanged();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add entry.');
    } finally {
      setAddSaving(false);
    }
  }

  function openEditEntry(entry: Record<string, unknown>) {
    setEditingEntryId(String(entry.id));
    const fields: Record<string, string> = {};
    for (const [key, val] of Object.entries(entry)) {
      if (key === 'id') continue;
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        fields[key] = val.split('T')[0];
      } else {
        fields[key] = val != null ? String(val) : '';
      }
    }
    setEditFields(fields);
    setError('');
  }

  async function handleSaveEntry() {
    if (!editingEntryId) return;
    setEditSaving(true);
    setError('');
    try {
      const body: Record<string, unknown> = {};
      const originalEntry = entries.find((e) => String(e.id) === editingEntryId);
      for (const [key, val] of Object.entries(editFields)) {
        if (key === 'id' || key === 'participantName' || key === 'total') continue;
        // Check if the field is for member-search (participantUserId)
        const fieldDef = getEntryFieldsForType(activity.type).find((f) => f.key === key);
        if (fieldDef?.inputType === 'member-search') {
          if (val) body[key] = val;
          continue;
        }
        const original = originalEntry?.[key];
        if (typeof original === 'number') {
          body[key] = parseFloat(val) || 0;
        } else {
          body[key] = val;
        }
      }
      await apiClient(`/fundraising/activities/${activity.id}/entries/${editingEntryId}`, {
        method: 'PATCH', body,
      });
      setEditingEntryId(null);
      setEditFields({});
      await fetchEntries();
      onDataChanged();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update entry.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDeleteEntry(entryId: string) {
    if (!confirm('Are you sure you want to delete this entry?')) return;
    setError('');
    try {
      await apiClient(`/fundraising/activities/${activity.id}/entries/${entryId}`, { method: 'DELETE' });
      await fetchEntries();
      onDataChanged();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to delete entry.');
    }
  }

  function handlePrint() {
    if (entries.length === 0) return;
    const cols = Object.keys(entries[0]).filter((k) => k !== 'id');
    const headerCells = cols.map((c) => `<th>${c.replace(/([A-Z])/g, ' $1').trim()}</th>`).join('');
    const rows = entries.map((entry) => {
      const cells = cols.map((c) => `<td>${formatCellValue(c, entry[c])}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html><head>
      <title>${activity.name} - Entries</title>
      <style>
        body { font-family: sans-serif; padding: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 8px 10px; border-bottom: 2px solid #333; font-size: 13px; }
        td { padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 12px; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        p { color: #666; font-size: 12px; margin-bottom: 16px; }
      </style>
    </head><body>
      <h1>${activity.name} — ${TYPE_LABELS[activity.type]}</h1>
      <p>${entries.length} entries | Printed ${new Date().toLocaleDateString()}</p>
      <table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  }

  function handleDownloadCsv() {
    if (entries.length === 0) return;
    const headers = Object.keys(entries[0]).filter((k) => k !== 'id');
    const csvRows = [headers.join(',')];
    for (const entry of entries) {
      const row = headers.map((h) => {
        const val = String(entry[h] ?? '').replace(/"/g, '""');
        return `"${val}"`;
      });
      csvRows.push(row.join(','));
    }
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activity.name.replace(/\s+/g, '-').toLowerCase()}-entries.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const displayColumns = entries.length > 0
    ? Object.keys(entries[0]).filter((k) => k !== 'id')
    : [];

  // Sort entries
  function handleSortClick(col: string) {
    if (sortColumn === col) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  }

  const sortedEntries = React.useMemo(() => {
    if (!sortColumn || entries.length === 0) return entries;
    return [...entries].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      let cmp = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [entries, sortColumn, sortDirection]);

  // Compute live total from entries (updates after each add without waiting for parent)
  const liveTotal = React.useMemo(() => {
    if (entries.length === 0) return activity.totalRaised ?? 0;
    // Sum revenue/amount/total columns from entries
    const sumKeys = Object.keys(entries[0]).filter((k) =>
      k.toLowerCase().includes('revenue') ||
      k.toLowerCase().includes('amount') ||
      k === 'total'
    );
    if (sumKeys.length === 0) return activity.totalRaised ?? 0;
    return entries.reduce((sum, e) => {
      for (const k of sumKeys) {
        sum += Number(e[k]) || 0;
      }
      return sum;
    }, 0);
  }, [entries, activity.totalRaised]);

  const progressPct = activity.goalAmount > 0
    ? Math.min((liveTotal / activity.goalAmount) * 100, 100)
    : 0;

  return (
    <div style={modalStyles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyles.modal} role="dialog" aria-modal="true" aria-label={`${activity.name} entries`}>
        {/* Header */}
        <div style={modalStyles.header}>
          <div style={modalStyles.headerLeft}>
            <span style={{ ...modalStyles.badge, backgroundColor: TYPE_COLORS[activity.type] }}>
              {TYPE_LABELS[activity.type]}
            </span>
            <h2 style={modalStyles.title}>{activity.name}</h2>
          </div>
          <button style={modalStyles.closeBtn} onClick={onClose} aria-label="Close modal">✕</button>
        </div>

        {/* Summary bar */}
        <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '2rem', alignItems: 'center', fontSize: '0.85rem' }}>
          <div>
            <span style={{ color: '#666' }}>Raised: </span>
            <span style={{ fontWeight: 700, color: '#1a6b3c' }}>
              ${liveTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div>
            <span style={{ color: '#666' }}>Goal: </span>
            <span style={{ fontWeight: 600 }}>
              ${Number(activity.goalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
          <div style={{ flex: 1, maxWidth: '200px' }}>
            <div style={{ width: '100%', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{ width: `${progressPct}%`, height: '100%', backgroundColor: progressPct >= 100 ? '#1a6b3c' : '#c9a84c', borderRadius: '4px' }} />
            </div>
          </div>
          <span style={{ fontSize: '0.8rem', color: '#666' }}>{progressPct.toFixed(1)}%</span>
          <div style={{ fontSize: '0.8rem', color: '#666' }}>
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>

        {/* Toolbar */}
        <div style={modalStyles.toolbar}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {canEdit && (
              <button
                style={modalStyles.btn}
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  const defaults: Record<string, string> = {};
                  getEntryFieldsForType(activity.type).forEach((f) => {
                    if (f.inputType === 'date') defaults[f.key] = today;
                  });
                  setShowAddForm(!showAddForm);
                  setAddFields(showAddForm ? {} : defaults);
                  setError('');
                }}
              >
                {showAddForm ? '✕ Cancel' : '+ New Entry'}
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={modalStyles.btnSecondary} onClick={handlePrint} disabled={entries.length === 0}>
              🖨️ Print
            </button>
            <button style={modalStyles.btnGold} onClick={handleDownloadCsv} disabled={entries.length === 0}>
              ⬇️ Download
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={modalStyles.body}>
          {error && <div style={formStyles.error}>{error}</div>}

          {/* Add Entry Form */}
          {showAddForm && (
            <form onSubmit={handleAddEntry} style={formStyles.container}>
              <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.95rem', color: '#0f2d1f' }}>New Entry</h3>
              <div style={formStyles.grid}>
                {getEntryFieldsForType(activity.type).map((field) => (
                  <div key={field.key} style={formStyles.field}>
                    <label style={formStyles.label}>{field.label}</label>
                    {field.inputType === 'member-search' ? (
                      <ChapterMemberSearchDropdown
                        value={addFields[field.key] || null}
                        onChange={(_memberId, fullName) => {
                          setAddFields({ ...addFields, [field.key]: fullName });
                          checkDuplicate(field.key, fullName);
                        }}
                        placeholder="Search Georgia chapter members..."
                      />
                    ) : (
                      <input
                        type={field.inputType}
                        step={field.inputType === 'number' ? '0.01' : undefined}
                        style={formStyles.input}
                        value={addFields[field.key] || ''}
                        onChange={(e) => {
                          setAddFields({ ...addFields, [field.key]: e.target.value });
                          checkDuplicate(field.key, e.target.value);
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
              {duplicateWarning && (
                <div style={{ padding: '8px 12px', backgroundColor: '#fffbea', border: '1px solid #f59e0b', borderRadius: '6px', marginTop: '0.75rem', fontSize: '0.82rem', color: '#92400e' }}>
                  {duplicateWarning}
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                <button type="submit" style={modalStyles.btn} disabled={addSaving}>
                  {addSaving ? 'Adding...' : 'Add Entry'}
                </button>
                <button type="button" style={modalStyles.btnSecondary} onClick={() => { setShowAddForm(false); setAddFields({}); setDuplicateWarning(''); }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Entries Table */}
          {loading ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>Loading entries...</p>
          ) : entries.length === 0 ? (
            <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>No entries yet. Click "+ New Entry" to add one.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyles.table}>
                <thead>
                  <tr>
                    {displayColumns.map((col) => (
                      <th
                        key={col}
                        style={{ ...tableStyles.th, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortClick(col)}
                        aria-sort={sortColumn === col ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
                      >
                        {col.replace(/([A-Z])/g, ' $1').trim()}
                        {sortColumn === col && (
                          <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>
                            {sortDirection === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </th>
                    ))}
                    {canEdit && <th style={tableStyles.th}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortedEntries.map((entry, idx) => {
                    const entryId = String(entry.id || idx);
                    const isEditing = editingEntryId === entryId;

                    if (isEditing) {
                      return (
                        <tr key={entryId} style={{ backgroundColor: '#fffbea' }}>
                          {displayColumns.map((col) => {
                            if (col === 'participantName' || col === 'total') {
                              return <td key={col} style={tableStyles.td}>{formatCellValue(col, entry[col])}</td>;
                            }
                            const fieldDef = getEntryFieldsForType(activity.type).find((f) => f.key === col);
                            return (
                              <td key={col} style={tableStyles.td}>
                                {fieldDef?.inputType === 'member-search' ? (
                                  <ChapterMemberSearchDropdown
                                    value={editFields[col] || null}
                                    onChange={(_memberId, fullName) => setEditFields({ ...editFields, [col]: fullName })}
                                    placeholder="Search Georgia chapter members..."
                                  />
                                ) : (
                                  <input
                                    type={typeof entry[col] === 'number' ? 'number' : col.toLowerCase().includes('date') ? 'date' : 'text'}
                                    step={typeof entry[col] === 'number' ? '0.01' : undefined}
                                    style={{ ...formStyles.input, width: '100%', minWidth: '80px' }}
                                    value={editFields[col] || ''}
                                    onChange={(e) => setEditFields({ ...editFields, [col]: e.target.value })}
                                  />
                                )}
                              </td>
                            );
                          })}
                          {canEdit && (
                            <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                              <button style={{ ...modalStyles.btn, fontSize: '0.75rem', padding: '0.3rem 0.6rem' }} onClick={handleSaveEntry} disabled={editSaving}>
                                {editSaving ? '...' : 'Save'}
                              </button>
                              <button style={{ ...modalStyles.btnSecondary, fontSize: '0.75rem', padding: '0.3rem 0.6rem', marginLeft: '0.25rem' }} onClick={() => { setEditingEntryId(null); setEditFields({}); }}>
                                Cancel
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    }

                    return (
                      <tr key={entryId}>
                        {displayColumns.map((col) => (
                          <td key={col} style={tableStyles.td}>
                            {formatCellValue(col, entry[col])}
                          </td>
                        ))}
                        {canEdit && (
                          <td style={{ ...tableStyles.td, whiteSpace: 'nowrap' }}>
                            <button
                              style={tableStyles.iconBtn}
                              onClick={() => openEditEntry(entry)}
                              title="Edit"
                              aria-label={`Edit entry ${idx + 1}`}
                            >✏️</button>
                            <button
                              style={tableStyles.iconBtn}
                              onClick={() => handleDeleteEntry(entryId)}
                              title="Delete"
                              aria-label={`Delete entry ${idx + 1}`}
                            >🗑️</button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>

                {/* Totals footer */}
                <tfoot>
                  <tr style={tableStyles.footer}>
                    {displayColumns.map((col, colIdx) => {
                      const isNumericCol = entries.every((e) => typeof e[col] === 'number' || e[col] === null || e[col] === undefined);
                      const isSummable = isNumericCol && (
                        col.toLowerCase().includes('miles') ||
                        col.toLowerCase().includes('revenue') ||
                        col.toLowerCase().includes('amount') ||
                        col.toLowerCase().includes('price') ||
                        col.toLowerCase().includes('quantity') ||
                        col.toLowerCase().includes('total') ||
                        col.toLowerCase().includes('sponsored')
                      );
                      if (colIdx === 0 && !isSummable) {
                        return <td key={col} style={{ padding: '0.6rem 0.75rem', color: '#0f2d1f' }}>TOTALS</td>;
                      }
                      if (isSummable) {
                        const total = entries.reduce((sum, e) => sum + (Number(e[col]) || 0), 0);
                        const isCurrency = col.toLowerCase().includes('revenue') || col.toLowerCase().includes('amount') || col.toLowerCase().includes('price') || col.toLowerCase().includes('total');
                        return (
                          <td key={col} style={{ padding: '0.6rem 0.75rem', color: '#0f2d1f' }}>
                            {isCurrency ? `$${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : total.toLocaleString()}
                          </td>
                        );
                      }
                      return <td key={col} style={{ padding: '0.6rem 0.75rem' }}>{colIdx === 0 ? 'TOTALS' : ''}</td>;
                    })}
                    {canEdit && <td style={{ padding: '0.6rem 0.75rem' }} />}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
